import { appSettings } from "./appSettings";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

/**
 * Initialize Application Insights
 */
export const appInsights = new ApplicationInsights({
  config: {
    connectionString: appSettings.INSTRUMENTATION_KEY || "",
    enableAutoRouteTracking: true,
  },
});
appInsights.loadAppInsights();

/**
 * Fetch logs from Application Insights using REST API.
 * @param query - Kusto Query Language (KQL) query string.
 */
export async function fetchLogs(query: string) {
  const appId = appSettings.INSIGHTS_APP_ID;
  const apiKey = appSettings.INSIGHTS_API_KEY;

  if (!appId || !apiKey) {
    throw new Error("Application ID or API key is missing in appSettings.");
  }

  const encodedQuery = encodeURIComponent(query);
  const url = `https://api.applicationinsights.io/v1/apps/${appId}/query?query=${encodedQuery}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "x-api-key": apiKey,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorDetails = await response.text();
      throw new Error(`Error fetching logs: ${response.status} - ${response.statusText}. Details: ${errorDetails}`);
    }

    const data = await response.json();
    return data.tables?.[0]?.rows || [];
  } catch (error) {
    console.error("Error fetching logs:", error);
    return [];
  }
}

/**
 * Generate CSV from logs fetched from Application Insights.
 * @param csvStartDate - Start date for logs
 * @param csvEndDate - End date for logs
 * @param reportType - "accessed" for users who accessed, "searched" for search logs
 */
export async function generateCSV(csvStartDate: Date | null, csvEndDate: Date | null, reportType: "accessed" | "searched") {
  if (!csvStartDate || !csvEndDate) {
    alert("Please select a valid date range in the modal.");
    return;
  }

  const startDate = csvStartDate.toISOString();
  const endDatePlusOne = new Date(csvEndDate);
  endDatePlusOne.setUTCDate(endDatePlusOne.getUTCDate() + 1);
  const endDate = endDatePlusOne.toISOString();

  let kqlQuery = "";

  if (reportType === "accessed") {
    kqlQuery = `
    customEvents
      | where timestamp >= datetime("${startDate}") 
      | where timestamp <= datetime("${endDate}")
      | where name == "OverviewPageVisit"
      | extend customDimensions = todynamic(customDimensions)
      | project timestamp, 
                userName=tostring(customDimensions.userName), 
                userEmail=tostring(customDimensions.userEmail)
  `;
  } else if (reportType === "searched") {
    kqlQuery = `
      customEvents
      | where timestamp >= datetime("${startDate}") 
      | where timestamp <= datetime("${endDate}")
      | where name == "SearchPerformed"
      | extend customDimensions = todynamic(customDimensions)
      | project timestamp, 
                userName=tostring(customDimensions.userName), 
                userEmail=tostring(customDimensions.userEmail), 
                airline=tostring(customDimensions.airline), 
                tailNumber=tostring(customDimensions.tailNumber), 
                flightNumber=tostring(customDimensions.flightNumber),
                messageType=tostring(customDimensions.messageType),
                smi=tostring(customDimensions.smi),
                fromTo=tostring(customDimensions.fromTo),
                isXml=tostring(customDimensions.isXml),
                startDate=tostring(customDimensions.startDate),
                endDate=tostring(customDimensions.endDate)
    `;
  }

  try {
    const logs = await fetchLogs(kqlQuery);

    if (logs.length === 0) {
      alert("No logs found in Application Insights.");
      return;
    }

    let csvHeader: any = [];
    let csvRows = [];

    if (reportType === "accessed") {
      csvHeader = ["Timestamp", "User Name", "User Email"];
      csvRows = logs.map((log: any[]) => [log[0], log[1], log[2]]);

      csvRows = logs.map((log: any[]) => [
        log[0], log[1], log[2], log[3], log[4], log[5],
        log[6], log[7], log[8], log[9], log[10], log[11]
      ]);
    }

    if (reportType === "searched") {
      csvHeader = [
        "Timestamp", "User Name", "User Email", "Airline", "AC Reg", "Flight Num",
        "Message Type", "SMI", "From/To", "Is Xml", "Start Date", "End Date"
      ];

      csvRows = logs.map((log: any[]) => {
        const [
          timestamp = "",
          userName = "",
          userEmail = "",
          airline = "",
          tailNumber = "",
          flightNumber = "",
          messageType = "",
          smi = "",
          fromTo = "",
          isXml = "",
          startDate = "",
          endDate = ""
        ] = log;

        return [
          timestamp,
          userName,
          userEmail,
          airline,
          tailNumber,
          flightNumber,
          messageType,
          smi,
          fromTo,
          isXml,
          startDate,
          endDate
        ];
      });
    }


    const csvContent: string = [
      csvHeader.join(","),
      ...csvRows.map((row: string[]) => row.join(",")),
    ].join("\n");

    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${reportType}_report_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("CSV Downloaded Successfully");
  } catch (error) {
    console.error("API Fetch Error:", error);
    alert("Error fetching logs from Application Insights.");
  }

}